<template>
  <v-container fluid class="dash pa-3 pa-sm-4 pa-md-6 pb-16">
    <!-- ─────────────── Hero ─────────────── -->
    <header class="dash-hero mb-4 mb-md-6">
      <div class="dash-hero__top">
        <div>
          <div class="dash-hero__eyebrow">
            <span class="dash-pulse" />
            Sentinel · Live intel
          </div>
          <h1 class="dash-hero__title">Risk briefing</h1>
          <p class="dash-hero__sub">
            Geopolitical, macro and conflict signals that may move the pairs you're watching.
          </p>
        </div>
        <div class="dash-hero__meta">
          <span v-if="dashboardStore.lastRefreshed" class="dash-hero__updated">
            <v-icon size="x-small" class="mr-1">mdi-clock-outline</v-icon>
            Updated {{ timeAgo(dashboardStore.lastRefreshed) }}
          </span>
          <v-btn
            v-if="dashboardStore.canRefresh"
            :loading="dashboardStore.refreshing"
            @click="refresh"
            variant="text"
            size="small"
            rounded="lg"
            class="dash-refresh"
          >
            <v-icon start size="small">mdi-refresh</v-icon>
            Refresh feed
          </v-btn>
        </div>
      </div>
    </header>

    <!-- ─────────────── KPI overview ─────────────── -->
    <section class="dash-overview mb-4">
      <div class="dash-overview__kpis">
        <div class="dash-kpi">
          <div class="dash-kpi__label">Last 24h</div>
          <div class="dash-kpi__value dash-mono">
            {{ dashboardStore.signals24hCount }}
            <span>signals</span>
          </div>
        </div>
        <div class="dash-kpi">
          <div class="dash-kpi__label">Active situations</div>
          <div class="dash-kpi__value dash-mono">
            {{ dashboardStore.topSituations.length }}
          </div>
        </div>
        <div class="dash-kpi dash-kpi--accent">
          <div class="dash-kpi__label">Top escalation</div>
          <div class="dash-kpi__value">
            <span class="dash-kpi__stage" :style="topEscalation ? { background: topEscalation.color + '20', color: topEscalation.color } : null">
              {{ topEscalation?.name || 'None' }}
            </span>
          </div>
        </div>
        <div class="dash-kpi">
          <div class="dash-kpi__label">Showing</div>
          <div class="dash-kpi__value dash-mono">
            {{ signals.length }}<span>/ {{ total || 0 }}</span>
          </div>
        </div>
      </div>

      <div v-if="domainList.length > 0" class="dash-radar">
        <div class="dash-radar__label">
          <v-icon size="x-small" class="mr-1">mdi-radar</v-icon>
          Domain pulse
        </div>
        <div class="dash-radar__bar">
          <div
            v-for="d in domainList"
            :key="d.key"
            class="dash-radar__seg"
            :style="{ width: d.pct + '%', background: d.color }"
            :title="`${d.label} · ${d.count} signals (${d.pct.toFixed(0)}%)`"
          />
        </div>
        <div class="dash-radar__legend">
          <span
            v-for="d in domainList"
            :key="d.key"
            class="dash-radar__item"
            :class="{ 'is-active': selectedDomain === d.key }"
            @click="toggleDomain(d.key)"
          >
            <span class="dash-radar__swatch" :style="{ background: d.color }" />
            {{ d.label }}
            <span class="dash-mono dash-radar__count">{{ d.count }}</span>
          </span>
        </div>
      </div>
    </section>

    <!-- ─────────────── Active situations ─────────────── -->
    <section v-if="dashboardStore.topSituations.length > 0" class="dash-section mb-4">
      <div class="dash-section__head">
        <h2 class="dash-section__title">
          <v-icon size="small" class="mr-2">mdi-fire</v-icon>
          Active situations
        </h2>
        <router-link to="/sentinel" class="dash-section__link">
          See all
          <v-icon size="x-small">mdi-arrow-right</v-icon>
        </router-link>
      </div>

      <div class="dash-situations">
        <article
          v-for="situation in dashboardStore.topSituations.slice(0, 4)"
          :key="situation.id"
          class="dash-situation"
          :style="{ '--stage': situation.escalation_stage?.color || '#94a3b8' }"
          @click="$router.push(`/sentinel/situations/${situation.id}`)"
        >
          <div class="dash-situation__head">
            <span class="dash-situation__stage">
              {{ situation.escalation_stage?.name || 'Unknown' }}
            </span>
            <span class="dash-situation__count dash-mono">
              {{ situation.signal_count }}
              <span>signals</span>
            </span>
          </div>
          <div class="dash-situation__title">
            {{ situation.title }}
          </div>
          <div class="dash-situation__foot">
            <v-icon size="x-small">mdi-arrow-right</v-icon>
            <span>Open</span>
          </div>
        </article>
      </div>
    </section>

    <!-- ─────────────── Signal feed ─────────────── -->
    <section class="dash-section">
      <div class="dash-section__head">
        <h2 class="dash-section__title">
          <v-icon size="small" class="mr-2">mdi-newspaper-variant-outline</v-icon>
          Intelligence feed
        </h2>
        <div class="dash-section__filters">
          <div class="dash-search">
            <v-icon size="small" class="dash-search__icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              @input="onSearchChange"
              type="text"
              placeholder="Search signals…"
              class="dash-search__input"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''; onSearchChange()"
              class="dash-search__clear"
              aria-label="Clear"
            >
              <v-icon size="x-small">mdi-close</v-icon>
            </button>
          </div>
          <button
            v-if="selectedDomain || searchQuery"
            class="dash-reset"
            @click="resetFilters"
          >
            <v-icon size="x-small">mdi-filter-off</v-icon>
            Reset
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading && signals.length === 0" class="dash-loading">
        <v-progress-circular indeterminate color="primary" size="36" />
        <p>Loading signals…</p>
      </div>

      <!-- Time-grouped signals -->
      <template v-else-if="signals.length > 0">
        <div
          v-for="group in groupedSignals"
          :key="group.key"
          class="dash-feed-group"
        >
          <div class="dash-feed-group__head">
            <span class="dash-feed-group__label">{{ group.label }}</span>
            <span class="dash-feed-group__count dash-mono">{{ group.signals.length }}</span>
            <span class="dash-feed-group__line" />
          </div>

          <article
            v-for="signal in group.signals"
            :key="signal.id"
            class="dash-signal"
            @click="openSignal(signal)"
          >
            <div class="dash-signal__sev">
              <span
                class="dash-signal__sev-num dash-mono"
                :style="{ background: domainPalette[signal.domain]?.color + '15', color: domainPalette[signal.domain]?.color }"
              >
                {{ Math.round(signal.severity_score || 0) }}
              </span>
            </div>

            <v-img
              v-if="signal.image_url"
              :src="signal.image_url"
              width="80"
              height="80"
              cover
              class="dash-signal__img"
            />

            <div class="dash-signal__body">
              <div class="dash-signal__meta">
                <span
                  class="dash-signal__domain"
                  :style="{ background: domainPalette[signal.domain]?.color + '14', color: domainPalette[signal.domain]?.color, borderColor: domainPalette[signal.domain]?.color + '30' }"
                >
                  {{ domainPalette[signal.domain]?.label || signal.domain }}
                </span>
                <span v-if="signal.region" class="dash-signal__region">
                  <v-icon size="x-small">mdi-map-marker-outline</v-icon>
                  {{ signal.region }}
                </span>
                <span class="dash-signal__time dash-mono">
                  {{ timeAgo(signal.published_at) }}
                </span>
              </div>
              <h3 class="dash-signal__title">{{ signal.title }}</h3>
              <p v-if="signal.content" class="dash-signal__excerpt">
                {{ signal.content }}
              </p>
              <div class="dash-signal__source">
                <v-icon size="x-small">mdi-rss</v-icon>
                {{ signal.source_name }}
              </div>
            </div>

            <v-icon class="dash-signal__chevron" size="small">mdi-open-in-new</v-icon>
          </article>
        </div>

        <!-- Pagination -->
        <div v-if="lastPage > 1" class="dash-pagination">
          <v-pagination
            v-model="page"
            :length="lastPage"
            :total-visible="5"
            density="comfortable"
            size="small"
            rounded
            @update:model-value="goToPage"
          />
        </div>
      </template>

      <!-- Empty state -->
      <div v-else-if="!loading" class="dash-empty">
        <v-icon size="56" color="grey-lighten-1">{{ hasActiveFilters ? 'mdi-filter-off' : 'mdi-newspaper-variant-outline' }}</v-icon>
        <p>{{ hasActiveFilters ? 'No signals match those filters.' : 'No signals yet.' }}</p>
        <v-btn
          v-if="hasActiveFilters"
          variant="text"
          @click="resetFilters"
          rounded="lg"
        >
          Reset filters
        </v-btn>
        <v-btn
          v-else-if="dashboardStore.canRefresh"
          color="primary"
          @click="refresh"
          rounded="lg"
        >
          <v-icon start>mdi-refresh</v-icon>
          Fetch signals
        </v-btn>
      </div>
    </section>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useSentinelDashboardStore } from '../stores/sentinelDashboard'
import axios from '../plugins/axios'

const dashboardStore = useSentinelDashboardStore()

const signals = ref([])
const loading = ref(false)
const page = ref(1)
const lastPage = ref(1)
const total = ref(0)
const searchQuery = ref('')
const selectedDomain = ref(null)
let searchDebounce = null

const domainPalette = {
  military:             { label: 'Military',     color: '#dc2626' },
  diplomatic:           { label: 'Diplomatic',   color: '#2563eb' },
  economic_warfare:     { label: 'Economic',     color: '#f97316' },
  internal_instability: { label: 'Instability',  color: '#9333ea' },
  energy_supply_chain:  { label: 'Energy',       color: '#0d9488' },
  cyber_information:    { label: 'Cyber',        color: '#06b6d4' },
  general:              { label: 'General',      color: '#94a3b8' }
}

const domainList = computed(() => {
  const breakdown = dashboardStore.domainBreakdown || {}
  const entries = Object.entries(breakdown)
  const totalSignals = entries.reduce((acc, [, count]) => acc + count, 0)
  if (totalSignals === 0) return []
  return entries
    .map(([key, count]) => ({
      key,
      count,
      pct: (count / totalSignals) * 100,
      label: domainPalette[key]?.label || key,
      color: domainPalette[key]?.color || '#94a3b8'
    }))
    .sort((a, b) => b.count - a.count)
})

const topEscalation = computed(() => {
  const stages = dashboardStore.topSituations
    .map(s => s.escalation_stage)
    .filter(Boolean)
  return stages[0] || null
})

const hasActiveFilters = computed(() => Boolean(selectedDomain.value || searchQuery.value))

// Group signals by time bucket
const groupedSignals = computed(() => {
  const now = Date.now()
  const groups = {
    lastHour: { key: 'lastHour', label: 'Last hour',   signals: [] },
    today:    { key: 'today',    label: 'Earlier today', signals: [] },
    week:     { key: 'week',     label: 'This week',    signals: [] },
    earlier:  { key: 'earlier',  label: 'Earlier',      signals: [] }
  }
  for (const s of signals.value) {
    if (!s.published_at) {
      groups.earlier.signals.push(s)
      continue
    }
    const diff = now - new Date(s.published_at).getTime()
    if (diff < 60 * 60 * 1000)              groups.lastHour.signals.push(s)
    else if (diff < 24 * 60 * 60 * 1000)    groups.today.signals.push(s)
    else if (diff < 7 * 24 * 60 * 60 * 1000) groups.week.signals.push(s)
    else                                     groups.earlier.signals.push(s)
  }
  return Object.values(groups).filter(g => g.signals.length > 0)
})

function timeAgo(dateInput) {
  if (!dateInput) return ''
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7)  return `${days}d ago`
  return `${Math.floor(days / 7)}w ago`
}

function openSignal(signal) {
  if (signal.url) {
    window.open(signal.url, '_blank', 'noopener,noreferrer')
  }
}

function toggleDomain(key) {
  selectedDomain.value = selectedDomain.value === key ? null : key
  fetchSignals(1)
}

function onSearchChange() {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => fetchSignals(1), 300)
}

function resetFilters() {
  selectedDomain.value = null
  searchQuery.value = ''
  fetchSignals(1)
}

async function fetchSignals(p = 1) {
  loading.value = true
  try {
    const params = { page: p, per_page: 20 }
    if (selectedDomain.value) params.domain = selectedDomain.value
    if (searchQuery.value)    params.search = searchQuery.value
    const response = await axios.get('/api/sentinel/signals', { params })
    signals.value = response.data.data
    page.value = response.data.meta.current_page
    lastPage.value = response.data.meta.last_page
    total.value = response.data.meta.total
  } catch {
    // silent
  } finally {
    loading.value = false
  }
}

function goToPage(p) {
  fetchSignals(p)
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function refresh() {
  await dashboardStore.refreshSignals()
  await fetchSignals(1)
}

onMounted(() => {
  dashboardStore.fetchDashboard()
  fetchSignals(1)
})
</script>

<style scoped>
/* Fonts are loaded once in index.html; palette/spacing come from assets/tokens.css */

.dash {
  font-family: var(--tc-font-sans);
  color: #1a2332;
  max-width: 1440px;
}
.dash-mono {
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

/* ─── Hero ─── */
.dash-hero__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.dash-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0d9488;
}
.dash-pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #14b8a6;
  box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.6);
  animation: dash-pulse 2s infinite;
}
@keyframes dash-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.6); }
  70%  { box-shadow: 0 0 0 8px rgba(20, 184, 166, 0); }
  100% { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0); }
}
.dash-hero__title {
  font-size: clamp(1.6rem, 2.4vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.05;
  margin: 8px 0 6px;
  color: #0f172a;
}
.dash-hero__sub {
  font-size: 0.95rem;
  color: #475569;
  max-width: 60ch;
  margin: 0;
  line-height: 1.55;
}
.dash-hero__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.dash-hero__updated {
  font-size: 0.78rem;
  color: var(--tc-muted);
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}

/* ─── Overview ─── */
.dash-overview {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}
@media (min-width: 960px) {
  .dash-overview { grid-template-columns: 1.4fr 1fr; gap: 32px; align-items: center; }
}
.dash-overview__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 600px) {
  .dash-overview__kpis { grid-template-columns: repeat(2, 1fr); gap: 12px; }
}
.dash-kpi {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 4px 14px;
  border-left: 2px solid #e2e8f0;
}
.dash-kpi--accent { border-left-color: #0d9488; }
.dash-kpi__label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--tc-muted);
}
.dash-kpi__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.dash-kpi__value span {
  font-size: 0.7rem;
  color: var(--tc-muted);
  font-weight: 500;
  letter-spacing: 0.02em;
}
.dash-kpi__stage {
  font-size: 0.85rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ─── Domain pulse ─── */
.dash-radar {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dash-radar__label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #475569;
  display: inline-flex;
  align-items: center;
}
.dash-radar__bar {
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: #f1f5f9;
}
.dash-radar__seg {
  height: 100%;
  transition: width 0.3s ease;
}
.dash-radar__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.75rem;
  color: #475569;
}
.dash-radar__item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
  transition: background 0.15s ease;
}
.dash-radar__item:hover { background: #f1f5f9; }
.dash-radar__item.is-active { background: #0f172a; color: #ffffff; }
.dash-radar__swatch {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.dash-radar__count {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.72rem;
}
.dash-radar__item.is-active .dash-radar__count { color: #ffffff; }

/* ─── Section ─── */
.dash-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.dash-section__title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
  margin: 0;
  display: inline-flex;
  align-items: center;
}
.dash-section__link {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0d9488;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.dash-section__link:hover { text-decoration: underline; }
.dash-section__filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ─── Active situations ─── */
.dash-situations {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.dash-situation {
  --stage: #94a3b8;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--stage);
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dash-situation:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}
.dash-situation__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.dash-situation__stage {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--stage);
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid;
  border-color: color-mix(in srgb, var(--stage) 35%, transparent);
  background: color-mix(in srgb, var(--stage) 10%, transparent);
}
.dash-situation__count {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0f172a;
}
.dash-situation__count span {
  font-size: 0.65rem;
  color: var(--tc-muted);
  margin-left: 2px;
  font-weight: 500;
}
.dash-situation__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dash-situation__foot {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: var(--tc-muted);
  font-weight: 600;
  margin-top: auto;
}

/* ─── Search & filter chrome ─── */
.dash-search {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 4px 10px 4px 12px;
  min-width: 220px;
  transition: border-color 0.15s ease;
}
.dash-search:focus-within {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.12);
}
.dash-search__icon { color: var(--tc-muted); }
.dash-search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.85rem;
  color: #0f172a;
  height: 28px;
}
.dash-search__input::placeholder { color: var(--tc-muted); }
.dash-search__clear {
  background: none;
  border: none;
  color: var(--tc-muted);
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: inline-flex;
}
.dash-search__clear:hover { background: #f1f5f9; color: #475569; }

.dash-reset {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  border: none;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
}
.dash-reset:hover { background: #e2e8f0; color: #0f172a; }

/* ─── Loading & empty ─── */
.dash-loading, .dash-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--tc-muted);
}
.dash-loading p, .dash-empty p { margin: 0; font-size: 0.9rem; }

/* ─── Feed group ─── */
.dash-feed-group { margin-bottom: 18px; }
.dash-feed-group__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.dash-feed-group__label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #475569;
}
.dash-feed-group__count {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--tc-muted);
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
}
.dash-feed-group__line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, #e2e8f0 0%, transparent 100%);
}

/* ─── Signal card ─── */
.dash-signal {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  position: relative;
}
.dash-signal:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  transform: translateY(-1px);
}
.dash-signal__sev {
  flex-shrink: 0;
}
.dash-signal__sev-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
}
.dash-signal__img {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  flex-shrink: 0;
}
.dash-signal__body {
  flex: 1;
  min-width: 0;
}
.dash-signal__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.dash-signal__domain {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid;
}
.dash-signal__region {
  font-size: 0.72rem;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.dash-signal__time {
  font-size: 0.7rem;
  color: var(--tc-muted);
  margin-left: auto;
}
.dash-signal__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dash-signal__excerpt {
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.45;
  margin: 4px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dash-signal__source {
  font-size: 0.7rem;
  color: var(--tc-muted);
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dash-signal__chevron {
  color: #cbd5e1;
  flex-shrink: 0;
  margin-top: 2px;
  transition: color 0.15s ease;
}
.dash-signal:hover .dash-signal__chevron { color: #0d9488; }

/* ─── Pagination ─── */
.dash-pagination {
  display: flex;
  justify-content: center;
  padding-top: 12px;
}

/* ─── Mobile ─── */
@media (max-width: 600px) {
  .dash-hero__title { font-size: 1.45rem; }
  .dash-kpi__value { font-size: 1.25rem; }
  .dash-signal__img { display: none; }
  .dash-signal { padding: 10px 12px; }
  .dash-section__head { gap: 8px; }
  .dash-section__filters { width: 100%; }
  .dash-search { flex: 1; min-width: 0; }
}
</style>
